import dayjs from 'dayjs';
import { BillProcessingStatus } from 'src/domain/customer-utility-bill/interface';
import { PROCESSING_STATUS_LABEL } from 'src/domain/customer-utility-bill/interface';
import { IBillSidebarFilters } from './billSidebarFilters';

export type TBillFilterKey = keyof IBillSidebarFilters;

export const BILL_FILTER_LABELS: Record<TBillFilterKey, string> = {
    period: 'Billing period',
    statuses: 'Status',
    verification: 'Verification',
    utilities: 'Utility',
};

const PERIOD_LABELS: Record<string, string> = {
    current_month: 'Current month',
    current_quarter: 'Current quarter',
    previous_month: 'Previous month',
    previous_quarter: 'Previous quarter',
};

const VERIFICATION_LABELS: Record<string, string> = {
    verified: 'Verified',
    unverified: 'Not verified',
};

const periodToDisplay = (period: string): string => {
    const key = period.split('::')[0];
    if (key === 'custom_range') {
        const raw = period.split('::')[1];
        const [start, end] = (raw || '').split('_');
        if (start && end) {
            return `${dayjs(start).format('DD/MMM/YYYY')} – ${dayjs(end).format('DD/MMM/YYYY')}`;
        }
        return 'Custom range';
    }

    return PERIOD_LABELS[key] ?? period;
};

/** A field is "active" (worth saving / displaying) when it holds a real selection. */
const isActiveValue = (value: unknown): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;

    return true;
};

/** Drop empty fields so the stored filter_values only carries real selections. */
export const cleanBillFilterValues = (filters: IBillSidebarFilters): Partial<IBillSidebarFilters> => {
    const result: Partial<IBillSidebarFilters> = {};

    (Object.keys(filters) as TBillFilterKey[]).forEach(key => {
        const value = filters[key];
        if (isActiveValue(value)) {
            (result as Record<string, unknown>)[key] = value;
        }
    });

    return result;
};

/** Human-readable value for a single filter field, for the "Selected Filters" chips. */
export const billFilterValueToDisplay = (key: string, value: unknown): string => {
    switch (key as TBillFilterKey) {
        case 'period':
            return periodToDisplay(String(value));
        case 'statuses':
            return (value as BillProcessingStatus[]).map(s => PROCESSING_STATUS_LABEL[s] ?? s).join(', ');
        case 'verification':
            return VERIFICATION_LABELS[String(value)] ?? String(value);
        case 'utilities':
            return (value as string[]).join(', ');
        default:
            return String(value);
    }
};
